import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Stepper from '@/v2/components/Stepper';
import CouponPaymentStep from './Steps/CouponPaymentStep';
import BuyerInfoStep from './Steps/BuyerInfoStep';
import {
  useCartState,
  useCartDispatch,
} from '../../../components/context/CartContext/CartContext';
import useFetch from 'use-http';
import { useCartData } from '../CartPage/context/CartDataContext';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import RegistrationErrorDialog from '../CartPage/Components/RegistrationErrorDialog';
import {
  buildCouponExponeaPayload,
  buildCouponTrackingData,
  persistBookingTrackingData,
  persistExponeaBookingPayload,
  pushBeginCheckoutTrackingData,
  pushExponeaBookingItemEvent,
  pushExponeaBookingResultEvent,
  readExponeaBookingPayload,
  clearExponeaBookingPayload,
} from '@/v2/utils/bookingTracking';

const STEBBY_REFERENCE_KEY = 'stebbyReference';
const STEBBY_COUPON_INPUT_KEY = 'stebbyCouponInput';

const PAYMENT_TYPES = {
  EBank: 'EBank',
  Stebby: 'Stebby',
};

const BACKEND_PAYMENT_TYPES = {
  Paysera: 'Paysera',
  Stebby: 'Stebby',
};

const isProduction = process.env.REACT_APP_IS_PRODUCTION === 'true';

const GENERIC_ERROR_MSG =
  'Atsiprašome, tačiau jūsų užsakymas nepavyko dėl nenumatytos klaidos. Bandykite dar kartą.';

const CouponCartPage = () => {
  const { coupon, loading, setLoading } = useCartData();
  const dispatch = useCartDispatch();
  const buyerFormRef = useRef();
  const {
    request: registrationRequest,
    response: registrationResponse,
    loading: isFinalizing,
  } = useFetch('/Registrations/Coupons');
  const {
    post: postStebby,
    get: getStebby,
    response: stebbyResponse,
    loading: isStebbyLoading,
  } = useFetch('/stebby');
  const [stepIndex, setStepIndex] = useState(0);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { step, orderId } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPES.EBank);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] =
    useState(GENERIC_ERROR_MSG);
  const history = useHistory();
  const stepTitles = useMemo(() => ['Mokėtojo duomenys', 'Apmokėjimas'], []);
  const quantityRef = useRef(1);
  const checkoutTrackedRef = useRef(null);
  const stebbyCallbackHandledRef = useRef(false);
  const cartState = useCartState();
  const isStebbySuccessRoute = step === 'stebby-success';
  const isStebbyCancelRoute = step === 'stebby-error' || step === 'stebby-cancel';

  const clearStebbySession = useCallback(() => {
    sessionStorage.removeItem(STEBBY_REFERENCE_KEY);
    sessionStorage.removeItem(STEBBY_COUPON_INPUT_KEY);
  }, []);
  const nextStep = async () => {
    if (stepIndex === 0) {
      const valid = await buyerFormRef.current.trigger();
      if (!valid) return;

      const formData = buyerFormRef.current.getValues();
      setLoading(true);
      await showPaymentOptions(formData);
    }

    // setStepIndex((i) => Math.min(i + 1, steps.length));
  };

  useEffect(() => {
    if (coupon == null && step != 1) {
      if (isStebbySuccessRoute || isStebbyCancelRoute) {
        return;
      }

      if (cartState.registrationId) {
        history.push('/payment/status/cancel');
      } else {
        history.push('/dovanu-kuponai');
      }
    }
  }, [cartState.registrationId, coupon, history, isStebbyCancelRoute, isStebbySuccessRoute, step]);

  const handleQuantityChange = (newQ) => {
    setQuantity(newQ); // updates UI on re-render
    quantityRef.current = newQ; // always up-to-date, sync immediately
  };

  useEffect(() => {
    if (!coupon) return;
    const itemId = coupon.couponId || coupon.id;
    if (checkoutTrackedRef.current === itemId) return;
    checkoutTrackedRef.current = itemId;

    pushBeginCheckoutTrackingData(
      buildCouponTrackingData({
        coupon,
        quantity: quantityRef.current,
      }),
    );
  }, [coupon]);

  const steps = useMemo(
    () => [
      {
        title: stepTitles[0],
        content: (
          <BuyerInfoStep
            ref={buyerFormRef}
            coupon={coupon}
            goNext={nextStep}
            paymentType={paymentType}
            onPaymentTypeChange={setPaymentType}
            isSubmitting={formSubmitting || isFinalizing || isStebbyLoading}
            onQuantityChange={(s) => {
              handleQuantityChange(s);
            }}
          />
        ),
      },
      {
        title: stepTitles[1],
        content: <CouponPaymentStep orderId={orderId} />,
      },
    ],
    [
      stepTitles,
      coupon,
      orderId,
      paymentType,
      formSubmitting,
      isFinalizing,
      isStebbyLoading,
    ],
  );

  useEffect(() => {
    if (step) {
      dispatch({ type: 'RESET_REGISTRATION_ID' });
      setStepIndex(Number(step));
    }
  }, [step]);

  const isCompleted = stepIndex === steps.length;

  const prevStep = () => setStepIndex((i) => Math.max(i - 1, 0));

  const buildPayload = useCallback(
    (formData, selectedPaymentType) => {
      const isStebby = selectedPaymentType === PAYMENT_TYPES.Stebby;
      const backendPaymentType = isStebby
        ? BACKEND_PAYMENT_TYPES.Stebby
        : BACKEND_PAYMENT_TYPES.Paysera;

      return {
        patientEmail: formData.payerEmail,
        patientName: formData.patientFirstName || formData.payerFirstName,
        patientSurname: formData.patientLastName || formData.payerLastName,
        patientPhoneNumber: formData.payerPhone,
        patientPersonalCode: formData.payerPersonalCode || null,
        amount: coupon.price,
        couponId: coupon.couponId,
        couponPriceId: coupon.priceId,
        count: quantityRef.current,
        PaymentType: backendPaymentType,
        paymentType: backendPaymentType,
        frontUrls: {
          cancelFrontURL: isStebby
            ? '/dovanu-kuponai/cart/stebby-error'
            : '/payment/status/cancel',
          acceptFrontUrl: isStebby
            ? '/dovanu-kuponai/cart/stebby-success'
            : '/dovanu-kuponai/cart/1',
          errorFrontUrl: isStebby
            ? '/dovanu-kuponai/cart/stebby-error'
            : '/payment/status/cancel',
        },
      };
    },
    [coupon],
  );

  const handleCouponSuccess = useCallback(
    ({ responseData, trackingData, exponeaPayload }) => {
      const couponOrderId = responseData?.couponOrderId;

      persistBookingTrackingData({
        ...trackingData,
        registrationId: couponOrderId,
        orderId: responseData.orderId || couponOrderId,
        bookingId: responseData.orderId || couponOrderId,
      });
      const orderExponeaPayload = exponeaPayload.map((item) =>
        ['booking_booking_id', 'booking_payment_gateway_id'].includes(item.name)
          ? { ...item, value: responseData.orderId || couponOrderId }
          : item);
      try {
        persistExponeaBookingPayload(orderExponeaPayload);
        pushExponeaBookingItemEvent(orderExponeaPayload);
      } catch { /* An optional marketing SDK must not prevent the payment redirect. */ }

      dispatch({
        type: 'SET_REGISTRATION_ID',
        registrationId: couponOrderId,
        orderType: 'coupon',
      });

      if (responseData?.paymentUrl) {
        window.location.href = responseData.paymentUrl;
        return;
      }

      history.replace(`/dovanu-kuponai/cart/1/${couponOrderId || ''}`);
      setStepIndex(1);
    },
    [dispatch, history],
  );

  const submitCouponOrder = useCallback(
    async ({ payload, trackingData, exponeaPayload }) => {
      const responseData = await registrationRequest.post('', payload);

      if (registrationResponse.status >= 500) {
        pushExponeaBookingResultEvent(exponeaPayload, 'Failed');
        setErrorDialogMessage(responseData?.message || GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
        return false;
      }

      if (registrationResponse.status >= 400) {
        pushExponeaBookingResultEvent(exponeaPayload, 'Failed');
        setErrorDialogMessage(responseData?.message || GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
        return false;
      }

      handleCouponSuccess({
        responseData,
        trackingData,
        exponeaPayload,
      });
      return true;
    },
    [handleCouponSuccess, registrationRequest, registrationResponse],
  );

  const startStebbyAuthorization = useCallback(
    async ({ payload, formData, trackingData, exponeaPayload }) => {
      try {
        const result = await postStebby('/identify/request-token?option=coupons');

        if (!stebbyResponse.ok || !result?.redirectUrl || !result?.reference) {
          console.error('❌ Failed to start Stebby authorization:', result);
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          return;
        }

        sessionStorage.setItem(STEBBY_REFERENCE_KEY, result.reference);
        sessionStorage.setItem(
          STEBBY_COUPON_INPUT_KEY,
          JSON.stringify({
            payload,
            formData,
            trackingData,
            exponeaPayload,
          }),
        );

        window.location.href = result.redirectUrl;
      } catch (err) {
        console.error('❌ Stebby authorization error:', err);
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
      }
    },
    [postStebby, stebbyResponse.ok],
  );

  async function showPaymentOptions(formData) {
    setFormSubmitting(true);

    try {
      const selectedPaymentType =
        !isProduction && paymentType === PAYMENT_TYPES.Stebby
          ? PAYMENT_TYPES.Stebby
          : PAYMENT_TYPES.EBank;
      const payload = buildPayload(formData, selectedPaymentType);
      const couponOrderId = payload.couponId;
      const trackingData = buildCouponTrackingData({
        coupon,
        orderId: couponOrderId,
        quantity: payload.count,
        value: payload.amount * payload.count,
      });
      const exponeaPayload = buildCouponExponeaPayload({
        coupon,
        formData,
        quantity: payload.count,
        orderId: couponOrderId,
      });

      if (selectedPaymentType === PAYMENT_TYPES.Stebby) {
        await startStebbyAuthorization({
          payload,
          formData,
          trackingData,
          exponeaPayload,
        });
        return;
      }

      await submitCouponOrder({ payload, trackingData, exponeaPayload });
    } finally {
      setFormSubmitting(false);
    }
  }

  useEffect(() => {
    if (isStebbyCancelRoute) {
      const exponeaPayload = readExponeaBookingPayload();
      pushExponeaBookingResultEvent(exponeaPayload, 'Cancelled');
      clearExponeaBookingPayload();
      clearStebbySession();
      setStepIndex(-1);
    }
  }, [clearStebbySession, isStebbyCancelRoute]);

  useEffect(() => {
    if (!isStebbySuccessRoute || stebbyCallbackHandledRef.current) return;

    stebbyCallbackHandledRef.current = true;

    const completeStebbyFlow = async () => {
      try {
        const reference = sessionStorage.getItem(STEBBY_REFERENCE_KEY);
        const couponInputRaw = sessionStorage.getItem(STEBBY_COUPON_INPUT_KEY);

        if (!reference || !couponInputRaw) {
          console.error('❌ Missing Stebby reference or coupon payload');
          clearStebbySession();
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          setStepIndex(-1);
          return;
        }

        const tokenResult = await getStebby(
          `/identify/token/${encodeURIComponent(reference)}`,
        );

        if (!stebbyResponse.ok || !tokenResult?.token) {
          console.error('❌ Failed to get Stebby token:', tokenResult);
          clearStebbySession();
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          setStepIndex(-1);
          return;
        }

        const stored = JSON.parse(couponInputRaw);
        const payloadWithStebbyToken = {
          ...stored.payload,
          PaymentType: BACKEND_PAYMENT_TYPES.Stebby,
          paymentType: BACKEND_PAYMENT_TYPES.Stebby,
          StebbyToken: tokenResult.token,
          StebbyReference: reference,
        };

        clearStebbySession();

        await submitCouponOrder({
          payload: payloadWithStebbyToken,
          trackingData: stored.trackingData,
          exponeaPayload: stored.exponeaPayload,
        });
      } catch (err) {
        console.error('❌ Failed to complete coupon Stebby flow:', err);
        clearStebbySession();
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
        setStepIndex(-1);
      }
    };

    completeStebbyFlow();
  }, [
    clearStebbySession,
    getStebby,
    isStebbySuccessRoute,
    stebbyResponse.ok,
    submitCouponOrder,
  ]);

  return (
    <div className="w-full max-w-[1440px] mx-auto h-full p-4 font-primary">
      <div className="mb-4">
        <Stepper
          steps={stepTitles}
          currentStep={isCompleted ? steps.length + 1 : stepIndex + 1}
        />
      </div>

      <div className="pb-4 min-h-40">
        {isStebbySuccessRoute ? (
          <div className="flex items-center justify-center min-h-40">
            <div className="text-lg font-medium">
              Tvirtinamas Stebby mokėjimas...
            </div>
          </div>
        ) : isStebbyCancelRoute || stepIndex === -1 ? (
          <div className="p-6 border rounded bg-white">
            <h3 className="text-lg font-semibold mb-2">Mokėjimas nepavyko</h3>
            <p className="text-gray-600">Bandykite dar kartą.</p>
          </div>
        ) : isCompleted ? (
          <div className="p-6 border rounded bg-white">
            <h3 className="text-lg font-semibold mb-2">Viskas paruošta!</h3>
            <p className="text-gray-600">
              Užsakymas pateiktas. Visi žingsniai užbaigti.
            </p>
          </div>
        ) : (
          steps[stepIndex]?.content
        )}
      </div>

      <RegistrationErrorDialog
        open={errorDialogOpen}
        onClose={() => {
          setErrorDialogOpen(false);
          history.push('/dovanu-kuponai');
        }}
        message={errorDialogMessage}
      />
    </div>
  );
};

export default CouponCartPage;
